import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Lock, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useBilling } from "@/context/BillingContext";
import { useAuth } from "@/context/AuthContext";
import { PLAN_PRICES, TRIAL_DAYS } from "@/context/BillingContext";
import { UpgradeCta, PlanHighlights } from "./UpgradeDialog";

// Lock panel (shared by the full-page and inline variants). Locked pages are noindex.
export function LockPanel({ feature, inline = false }) {
  const { t } = useTranslation();
  return (
    <div data-testid={`paywall-${feature}`} className={`${inline ? "rounded-lg border border-white/10 bg-card p-6 sm:p-8" : "max-w-xl mx-auto px-4 py-16 sm:py-24"} text-center animate-fade-up`}>
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="mx-auto grid place-items-center w-14 h-14 rounded-full bg-primary/15 text-primary mb-4"><Lock size={26} /></div>
      <h1 className="font-heading font-black text-2xl sm:text-3xl">{t(`billing.lockTitles.${feature}`, t("billing.lock.title"))}</h1>
      <p className="text-slate-400 mt-2 mb-6">{t("billing.lock.body", { price: PLAN_PRICES.individual, days: TRIAL_DAYS })}</p>
      <div className="rounded-md border border-white/10 bg-background/40 p-4 mb-6 inline-block"><PlanHighlights feature={feature} /></div>
      <UpgradeCta feature={feature} />
      <p className="text-xs text-slate-500 mt-6">{t("billing.lock.freeNote")}</p>
    </div>
  );
}

// Route/section guard: renders children for paid users (or exempt roles), a lock otherwise.
export default function RequirePaid({ feature, children, inline = false }) {
  const { loading: authLoading, user } = useAuth();
  const billing = useBilling();
  const pending = authLoading || user === null || (user && billing.loading);
  if (pending) return <div className={inline ? "py-16 grid place-items-center" : "h-screen grid place-items-center bg-background"}><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (billing.access) return children;
  if (inline) return <LockPanel feature={feature} inline />;
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1"><LockPanel feature={feature} /></main>
      <Footer />
    </div>
  );
}
